import { Loader2, Eye, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { mxn, fdate } from "@/lib/format";
import { MEDIOS_PAGO } from "@/lib/catalogos_maestros";

// Espejo de `cartera_migrada.MIN_MOTIVO`.
export const MIN_MOTIVO_MIGRACION = 10;
const UUID_RE = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;

export const MIG_VACIA = {
  activo: false, motivo: "", numero_anterior: "", cuotas_pagadas: "0", medio_pago: "",
  parcial_monto: "", parcial_fecha: "", uuid_ppd: "",
};

export const bodyMigracion = (mig, fecha) => ({
  cartera_migrada: true,
  fecha_desembolso: fecha,
  motivo_migracion: mig.motivo.trim(),
  numero_credito_anterior: mig.numero_anterior.trim(),
  cuotas_pagadas: Number(mig.cuotas_pagadas || 0),
  medio_pago_historico: mig.medio_pago,
  pago_parcial: Number(mig.parcial_monto) > 0
    ? { monto: Number(mig.parcial_monto), fecha: mig.parcial_fecha } : {},
  uuid_ppd_anterior: mig.uuid_ppd.trim().toUpperCase(),
});

// Primer error que el backend devolvería, para no hacer el viaje (null = listo).
export const errorMigracion = (mig) => {
  if (mig.motivo.trim().length < MIN_MOTIVO_MIGRACION)
    return `Escribe el motivo de la migración (mínimo ${MIN_MOTIVO_MIGRACION} caracteres).`;
  const conPagos = Number(mig.cuotas_pagadas || 0) > 0 || Number(mig.parcial_monto) > 0;
  if (conPagos && !mig.medio_pago) return "Elige el medio de pago de las cuotas ya pagadas.";
  if (Number(mig.parcial_monto) > 0 && !mig.parcial_fecha) return "Captura la fecha del pago parcial.";
  const u = mig.uuid_ppd.trim().toUpperCase();
  if (u && !UUID_RE.test(u)) return "El UUID de la factura PPD anterior no tiene el formato del SAT.";
  return null;
};

const Campo = ({ label, children, ayuda }) => (
  <div>
    <Label className="text-xs text-indigo-950">{label}</Label>
    {children}
    {ayuda && <p className="mt-1 text-[11px] text-indigo-800/80">{ayuda}</p>}
  </div>
);

export function MigracionCarteraPanel({ sel, mig, setMig, fecha, hoy, preview, previewing, onPreview }) {
  const set = (k) => (e) => setMig((m) => ({ ...m, [k]: e?.target ? e.target.value : e }));
  const plazo = Number(sel?.plazo || sel?.tabla_amortizacion?.length || 0);
  return (
    <div className="space-y-3 rounded-md border border-indigo-300 bg-indigo-50/70 p-3" data-testid="migracion-panel">
      <Campo label={<>Motivo de la migración <span className="text-red-600">*</span></>}
        ayuda={`Obligatorio, mínimo ${MIN_MOTIVO_MIGRACION} caracteres. Queda en la bitácora.`}>
        <Textarea data-testid="migracion-motivo" rows={2} value={mig.motivo} onChange={set("motivo")}
          placeholder="Ej. migración de la cartera vigente del sistema anterior al cierre de septiembre" />
      </Campo>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Campo label="No. de crédito en el sistema anterior" ayuda="Con este número se reporta al SIC.">
          <Input data-testid="migracion-numero-anterior" value={mig.numero_anterior}
            onChange={set("numero_anterior")} placeholder="Ej. SIS-0001234" />
        </Campo>
        <Campo label={`Cuotas ya pagadas (0 a ${plazo})`}
          ayuda="Se registra un pago por cuota con su fecha de vencimiento, sin moratorio.">
          <Input type="number" min={0} max={plazo} data-testid="migracion-cuotas-pagadas"
            value={mig.cuotas_pagadas} onChange={set("cuotas_pagadas")} />
        </Campo>
      </div>
      <Campo label="Medio de pago de esas cuotas" ayuda="El efectivo de antes del trimestre en curso no cuenta para PLD.">
        <Select value={mig.medio_pago} onValueChange={set("medio_pago")}>
          <SelectTrigger data-testid="migracion-medio-pago"><SelectValue placeholder="Selecciona" /></SelectTrigger>
          <SelectContent>
            {MEDIOS_PAGO.map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </Campo>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Campo label="Pago parcial adicional (opcional)" ayuda="Abono a la cuota siguiente, menor que la cuota.">
          <Input type="number" min={0} step="0.01" data-testid="migracion-parcial-monto"
            value={mig.parcial_monto} onChange={set("parcial_monto")} placeholder="0.00" />
        </Campo>
        <Campo label="Fecha del pago parcial">
          <Input type="date" data-testid="migracion-parcial-fecha" value={mig.parcial_fecha}
            min={fecha} max={hoy} disabled={!(Number(mig.parcial_monto) > 0)} onChange={set("parcial_fecha")} />
        </Campo>
      </div>
      <Campo label="UUID de la factura PPD del sistema anterior (opcional)"
        ayuda="Con UUID no se emite otra factura PPD ni PUE de sus pagos. Los pagos históricos nunca se timbran.">
        <Input data-testid="migracion-uuid-ppd" value={mig.uuid_ppd} onChange={set("uuid_ppd")}
          className="font-mono uppercase" placeholder="XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX" />
      </Campo>
      <Button type="button" variant="outline" size="sm" onClick={onPreview} disabled={previewing}
        className="border-indigo-400 bg-white text-indigo-900 hover:bg-indigo-100" data-testid="migracion-vista-previa-btn">
        {previewing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Eye className="mr-1.5 h-4 w-4" />}
        Vista previa del saldo
      </Button>
      {preview && <VistaPreviaMigracion preview={preview} />}
    </div>
  );
}

const Dato = ({ label, valor, testid, alerta }) => (
  <div className="rounded border border-indigo-200 bg-white px-2 py-1.5">
    <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
    <p className={`text-sm font-semibold ${alerta ? "text-rose-700" : "text-slate-900"}`} data-testid={testid}>{valor}</p>
  </div>
);

function VistaPreviaMigracion({ preview }) {
  const p = preview;
  return (
    <div className="space-y-2" data-testid="migracion-vista-previa">
      <p className="flex items-center gap-1.5 text-xs font-semibold text-indigo-950">
        <History className="h-3.5 w-3.5" /> Así quedará el crédito (todavía no se guarda nada)
      </p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Dato label="Saldo insoluto" valor={mxn(p.saldo_insoluto)} testid="migracion-pv-saldo" />
        <Dato label="Monto vencido" valor={mxn(p.monto_vencido)} testid="migracion-pv-vencido" alerta={p.monto_vencido > 0} />
        <Dato label="Días de atraso" valor={p.dias_atraso} testid="migracion-pv-atraso" alerta={p.dias_atraso > 0} />
        <Dato label="Próximo pago" valor={p.proximo_pago ? fdate(p.proximo_pago) : "—"} testid="migracion-pv-proximo" />
      </div>
      <div className="max-h-48 overflow-y-auto rounded border border-indigo-200 bg-white">
        <table className="w-full text-[11px]" data-testid="migracion-pv-cuotas">
          <thead className="sticky top-0 bg-slate-50 text-slate-500">
            <tr><th className="px-2 py-1 text-left">#</th><th className="px-2 py-1 text-left">Vence</th>
              <th className="px-2 py-1 text-right">Cuota</th><th className="px-2 py-1 text-right">Pagado</th>
              <th className="px-2 py-1 text-left">Estatus</th></tr>
          </thead>
          <tbody>
            {(p.cuotas || []).map((c) => (
              <tr key={c.no} className="border-t border-slate-100" data-testid={`migracion-pv-cuota-${c.no}`}>
                <td className="px-2 py-0.5">{c.no}</td><td className="px-2 py-0.5">{fdate(c.fecha_pago)}</td>
                <td className="px-2 py-0.5 text-right">{mxn(c.total)}</td><td className="px-2 py-0.5 text-right">{mxn(c.pagado)}</td>
                <td className={`px-2 py-0.5 ${c.estatus === "Vencida" ? "font-semibold text-rose-700" : c.estatus === "Pagada" ? "text-emerald-700" : ""}`}>{c.estatus}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] font-medium text-indigo-900" data-testid="migracion-pv-aviso">{p.aviso}</p>
    </div>
  );
}
